import * as _ from 'lodash-es';
import type { Bounds } from 'quill/core';
import Quill from 'quill/core';
import { useCallback, useEffect, useRef, useState } from 'react';
import closestLine from './closestLine';
import getRelativeBoundingRect from './controls/toolbar/getRelativeBoundingRect';
import type { Blot, Line } from './types';
import getReferenceNodeForBounds from './utils/getReferenceNodeForBounds';
import listenBody from './utils/listenBody';

const Module = Quill.import('core/module');
export const ignoreHoveredBlotLeaveClassName = 'ql-ignore-hovered-blot-leave';

const shouldIgnoreNode = (node: Node) => {
  if (!(node instanceof Element)) return true;
  return !!node.closest(`.${ignoreHoveredBlotLeaveClassName}`);
};

const EVENTS = {
  HOVERED: 'hovered-blot-listener-hovered',
  PERSIST: 'hovered-blot-listener-persist',
};

type State = {
  ids: Set<string>;
  hoveredBlot: Line;
};

export type HoveredBlotListenerSetting<
  Context extends Record<string, unknown>,
  HoveredBlot extends Line = Line,
> = {
  isHoveredBlot(hoveredBlot: Line, args: { node: Node; quill: Quill }): boolean;
  getContext?(blot: HoveredBlot, quill: Quill): Context;
};

type BlotFromSetting<
  Setting extends HoveredBlotListenerSetting<Record<string, unknown>>,
> =
  Setting extends HoveredBlotListenerSetting<
    Record<string, unknown>,
    infer InferredBlot
  >
    ? InferredBlot
    : never;

type ContextFromSetting<
  Setting extends HoveredBlotListenerSetting<Record<string, unknown>>,
> =
  Setting extends HoveredBlotListenerSetting<infer InferredContext>
    ? InferredContext
    : never;

const stateSame = (state1: State | null, state2: State | null) => {
  if (!state1 && !state2) return true;
  if (state1 && state2)
    return (
      _.isEqual(state1.ids, state2.ids) &&
      state1.hoveredBlot === state2.hoveredBlot
    );
  return false;
};

class HoveredBlotListener extends Module {
  private settings: Record<
    string,
    HoveredBlotListenerSetting<Record<string, unknown>>
  >;
  private state: State | null;
  private persistedState: State | null;

  constructor(quill: Quill, options: Record<string, unknown>) {
    super(quill, options);

    this.settings = {};

    this.state = null;

    // Keeps the current hovered blot unchanged during mouse movement.
    // This is useful when the user is interacting with an associated view node.
    // ex dropdown menu is open.
    this.persistedState = null;

    this.setupListener();
  }

  addSetting(
    id: string,
    setting: HoveredBlotListenerSetting<Record<string, unknown>>,
  ) {
    this.settings[id] = setting;
  }

  removeSetting(id: string) {
    delete this.settings[id];
  }

  emitState() {
    const { state, persistedState, quill, settings } = this;

    const { ids = new Set(), hoveredBlot } = persistedState ?? state ?? {};

    const bounds = hoveredBlot
      ? getRelativeBoundingRect(
          getReferenceNodeForBounds(hoveredBlot),
          quill.container,
        )
      : null;

    Object.keys(settings).forEach((id) => {
      if (hoveredBlot && ids.has(id)) {
        quill.emitter.emit(EVENTS.HOVERED, {
          id,
          hoveredBlot,
          bounds,
          context: settings[id]?.getContext?.(hoveredBlot, quill),
        });
      } else {
        quill.emitter.emit(EVENTS.HOVERED, { id });
      }
    });
  }

  setState(state: State | null) {
    if (stateSame(this.state, state)) return;

    this.state = state;
    this.emitState();
  }

  getMatchedState(blot: Line | null, node: Node): State | null {
    if (!blot) return null;

    const ids = new Set(
      Object.keys(this.settings).filter((id) =>
        this.settings[id]?.isHoveredBlot(blot, { node, quill: this.quill }),
      ),
    );

    if (ids.size > 0) {
      return { hoveredBlot: blot, ids };
    }
    return null;
  }

  setupListener() {
    const { quill } = this;

    quill.scroll.domNode.addEventListener('mouseover', ({ target }) => {
      if (!(target instanceof Node)) return;

      const blot = closestLine(quill.scroll.find(target, true));
      this.setState(this.getMatchedState(blot, target));
    });

    listenBody(
      'mouseout',
      ({ relatedTarget }) => {
        if (!this.state || !(relatedTarget instanceof Node)) return;

        if (
          !quill.root.contains(relatedTarget) &&
          !shouldIgnoreNode(relatedTarget)
        ) {
          this.setState(null);
        }
      },
      quill,
    );

    quill.on(Quill.events.TEXT_CHANGE, () => {
      if (!this.state) return;

      const isExisting = this.state.hoveredBlot.offset(quill.scroll) >= 0;
      if (isExisting) {
        this.emitState();
      } else {
        this.setState(null);
      }
    });

    quill.on(EVENTS.PERSIST, (persist: boolean) => {
      this.persistedState = persist ? this.state : null;
      this.emitState();
    });
  }
}

type HoveredBlotListenerState<Context, HoveredBlot extends Blot> = {
  bounds?: Bounds;
  hoveredBlot?: HoveredBlot;
  context?: Context;
};

export const useHoveredBlotListener = <
  Settings extends HoveredBlotListenerSetting<Record<string, unknown>>,
>(
  quill: Quill | null,
  setting: Settings,
): HoveredBlotListenerState<
  ContextFromSetting<Settings>,
  BlotFromSetting<Settings>
> & {
  setPersist: (persist: boolean) => void;
} => {
  const idRef = useRef<string | null>(null);
  if (idRef.current === null) {
    idRef.current = _.uniqueId('hoveredBlotListener');
  }

  const [state, setState] = useState<
    HoveredBlotListenerState<
      ContextFromSetting<Settings>,
      BlotFromSetting<Settings>
    >
  >({});

  useEffect(() => {
    const hoveredBlotListenerModule = quill?.getModule(
      'hoveredBlotListener',
    ) as HoveredBlotListener | undefined;
    if (!hoveredBlotListenerModule) return undefined;

    const id = idRef.current as string;
    hoveredBlotListenerModule.addSetting(id, setting);
    return () => hoveredBlotListenerModule.removeSetting(id);
  }, [quill, setting]);

  useEffect(() => {
    if (!quill) return undefined;
    const listener = ({ id, ...args }: { id: string }) => {
      if (idRef.current === id) {
        setState((current) => (_.isEqual(args, current) ? current : args));
      }
    };

    quill.on(EVENTS.HOVERED, listener);
    return () => {
      quill.off(EVENTS.HOVERED, listener);
    };
  }, [quill]);

  const setPersist = useCallback(
    (persist: boolean) => quill?.emitter.emit(EVENTS.PERSIST, persist),
    [quill],
  );

  return { ...state, setPersist };
};

export default HoveredBlotListener;
